// v49.0 — LABOURCORE_CRYPTO port.
// Source: v48.311 lines 11283-11580. Same behaviour byte-for-byte; just
// retyped with TypeScript and exported as ES module.
// AES-256-GCM session-keyed encryption-at-rest for sensitive tables.
// Key lives in sessionStorage["lc_session_key"]; clearSessionKey() drops
// it on sign-out. v48.308 + v48.309 added orphan-blob auto-cleanup and
// the "return empty string on decrypt failure" robustness fix.

const SESSION_KEY_NAME = "lc_session_key";
const HAS_CRYPTO = typeof crypto !== "undefined" && !!crypto.subtle;

let _key: CryptoKey | null = null;
let _ready = false;
let _readyPromise: Promise<void> | null = null;

const _toBase64 = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};
const _fromBase64 = (str: string): ArrayBuffer => {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

async function init(): Promise<void> {
  if (!HAS_CRYPTO) { console.warn("[CRYPTO] WebCrypto not available — encryption disabled"); return; }
  try {
    const stored = sessionStorage.getItem(SESSION_KEY_NAME);
    if (stored) {
      _key = await crypto.subtle.importKey("jwk", JSON.parse(stored), { name: "AES-GCM" }, true, ["encrypt", "decrypt"]);
    } else {
      _key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
      const jwk = await crypto.subtle.exportKey("jwk", _key);
      sessionStorage.setItem(SESSION_KEY_NAME, JSON.stringify(jwk));
    }
    _ready = true;
    console.log("[CRYPTO] AES-256-GCM session key ready");
  } catch (e) {
    console.warn("[CRYPTO] Init failed — encryption disabled", e);
    _ready = false;
  }
}

_readyPromise = init();

export async function encrypt(plaintext: string): Promise<string> {
  if (!_ready || !_key || !plaintext) return plaintext;
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);
    const cipherBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, _key, encoded);
    const combined = new Uint8Array(iv.length + cipherBuf.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuf), iv.length);
    return "enc:" + _toBase64(combined.buffer);
  } catch (e) {
    console.warn("[CRYPTO] encrypt failed", e);
    return plaintext;
  }
}

/* v48.309 — returns "" on decryption failure (was: raw enc:... blob).
   Orphan blobs encrypted with a previous session key now surface as
   empty fields in the UI instead of garbled ciphertext. */
export async function decrypt(blob: string): Promise<string> {
  if (!_ready || !_key || !blob || typeof blob !== "string" || !blob.startsWith("enc:")) return blob;
  try {
    const combined = new Uint8Array(_fromBase64(blob.slice(4)));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const plainBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, _key, ciphertext);
    return new TextDecoder().decode(plainBuf);
  } catch (e) {
    console.warn("[CRYPTO] decrypt failed — returning empty string", e);
    return "";
  }
}

export const SENSITIVE_FIELDS: Record<string, string[]> = {
  mailboxes:          ["body", "subject"],
  notifications:      ["msg"],
  disputes:           ["body", "subject"],
  siteOps_incidents:  ["description", "actions"],
};

export async function encryptObj<T extends Record<string, unknown>>(obj: T, fields: string[]): Promise<T> {
  if (!_ready || !obj || !fields) return obj;
  const clone = { ...obj } as Record<string, unknown>;
  for (const f of fields) {
    const v = clone[f];
    if (typeof v === "string" && v) clone[f] = await encrypt(v);
  }
  return clone as T;
}

export async function decryptObj<T extends Record<string, unknown>>(obj: T, fields: string[]): Promise<T> {
  if (!_ready || !obj || !fields) return obj;
  const clone = { ...obj } as Record<string, unknown>;
  for (const f of fields) {
    const v = clone[f];
    if (typeof v === "string" && v.startsWith("enc:")) clone[f] = await decrypt(v);
  }
  return clone as T;
}

export function clearSessionKey(): void {
  _key = null;
  _ready = false;
  try { sessionStorage.removeItem(SESSION_KEY_NAME); } catch (_) { /* ignore */ }
}

export function whenReady(): Promise<void> {
  return _readyPromise || Promise.resolve();
}

export function isReady(): boolean { return _ready; }

/* Window export so v48.x DevTools diagnostics + the shellSignOut helper
   can still reach the module during the migration. */
export const LABOURCORE_CRYPTO = {
  init, encrypt, decrypt, encryptObj, decryptObj,
  clearSessionKey, whenReady, isReady,
  SENSITIVE_FIELDS,
};

if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).LABOURCORE_CRYPTO = LABOURCORE_CRYPTO;
}

export default LABOURCORE_CRYPTO;
